(() => {
  const desktop = window.matchMedia('(min-width: 990px)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 1500));

  function initHeader() {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:80px;pointer-events:none';
    document.body.prepend(sentinel);
    new IntersectionObserver(([entry]) => {
      header.classList.toggle('is-solid', !entry.isIntersecting);
    }).observe(sentinel);

    const megas = header.querySelectorAll('[data-mega]');
    const syncOpen = () => header.classList.toggle('is-open', [...megas].some((d) => d.open));
    megas.forEach((d) => {
      d.addEventListener('toggle', syncOpen);
      d.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && d.open) { d.open = false; d.querySelector('summary').focus(); }
      });
    });
    document.addEventListener('click', (e) => {
      megas.forEach((d) => { if (d.open && !d.contains(e.target)) d.open = false; });
    });

    const drawer = document.getElementById('MenuDrawer');
    const opener = header.querySelector('[data-drawer-open]');
    if (drawer && opener && typeof drawer.showModal === 'function') {
      opener.addEventListener('click', () => { drawer.showModal(); opener.setAttribute('aria-expanded', 'true'); });
      drawer.querySelector('[data-drawer-close]').addEventListener('click', () => drawer.close());
      drawer.addEventListener('close', () => { opener.setAttribute('aria-expanded', 'false'); opener.focus(); });
      drawer.addEventListener('click', (e) => { if (e.target === drawer) drawer.close(); });
    }
  }

  function initLocalization() {
    document.querySelectorAll('[data-autosubmit]').forEach((el) => {
      el.addEventListener('change', () => el.form && el.form.submit());
    });
  }

  function setToggleState(btn, playing, labels) {
    if (!btn) return;
    btn.setAttribute('aria-pressed', String(playing));
    const pause = btn.querySelector('[data-icon-pause]');
    const play = btn.querySelector('[data-icon-play]');
    if (pause) pause.hidden = !playing;
    if (play) play.hidden = playing;
    const label = btn.querySelector('[data-toggle-label]');
    if (label && labels) label.textContent = playing ? labels.pause : labels.play;
  }

  function initHeroVideo() {
    const hero = document.querySelector('[data-hero]');
    if (!hero) return;
    const video = hero.querySelector('video[data-src]');
    const btn = hero.querySelector('[data-hero-toggle]');
    if (!video || reduced.matches) return;
    const conn = navigator.connection || {};
    if (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '')) return;
    const labels = { pause: 'Pause video', play: 'Play video' };
    const start = () => {
      const source = document.createElement('source');
      // Speed polish (3 Nov 2026): the 9:16 frame is ~440 px wide on most laptops, so the 480p
      // rendition is sharp enough there too. Only fetch 720p when the frame needs more than ~520 device px.
      const frameW = (video.parentElement && video.parentElement.clientWidth || 0) * (window.devicePixelRatio || 1);
      const useLight = video.dataset.srcMobile && (!desktop.matches || frameW <= 520);
      source.src = useLight ? video.dataset.srcMobile : video.dataset.src;
      source.type = 'video/mp4';
      video.appendChild(source);
      video.load();
      video.play().then(() => {
        video.classList.add('is-ready');
        if (btn) { btn.hidden = false; setToggleState(btn, true, labels); }
      }).catch(() => {});
    };
    if (document.readyState === 'complete') idle(start); else window.addEventListener('load', () => idle(start), { once: true });
    let userPaused = false;
    if (btn) btn.addEventListener('click', () => {
      if (video.paused) { userPaused = false; video.play(); setToggleState(btn, true, labels); } else { userPaused = true; video.pause(); setToggleState(btn, false, labels); }
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        if (!video.classList.contains('is-ready') || userPaused) return;
        if (entry.isIntersecting) video.play().catch(() => {}); else video.pause();
      }, { threshold: 0.15 }).observe(video);
    }
  }

  function initTapVideos() {
    document.querySelectorAll('[data-tap-video]').forEach((frame) => {
      const video = frame.querySelector('video[data-src]');
      const btn = frame.querySelector('[data-tap-toggle]');
      if (!video || !btn) return;
      const labels = { pause: 'Pause clip', play: 'Play clip' };
      btn.addEventListener('click', () => {
        if (!video.querySelector('source')) {
          const s = document.createElement('source');
          s.src = video.dataset.src; s.type = 'video/mp4';
          video.appendChild(s); video.load();
        }
        if (video.paused) { video.play(); frame.classList.add('is-playing'); setToggleState(btn, true, labels); }
        else { video.pause(); setToggleState(btn, false, labels); }
      });
    });
  }

  function initCardHover() {
    if (!fine.matches || !desktop.matches) return;
    const load = (media) => {
      if (media.dataset.hoverLoaded) return;
      const tpl = media.querySelector('template[data-hover-template]');
      if (!tpl) return;
      media.appendChild(tpl.content.cloneNode(true));
      media.dataset.hoverLoaded = '1';
    };
    document.addEventListener('pointerover', (e) => {
      const media = e.target.closest && e.target.closest('[data-hover-card]');
      if (media) load(media);
    });
  }

  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach((track) => {
      const slides = [...track.querySelectorAll('[data-slide]')];
      if (!slides.length) return;
      const scope = track.closest('section') || document;
      const counterWrap = track.id ? scope.querySelector('[data-carousel-counter="' + track.id + '"]') : null;
      const counter = (counterWrap || track.parentElement).querySelector('[data-carousel-current]');
      const sel = (attr) => track.id ? scope.querySelector('[' + attr + '][aria-controls="' + track.id + '"]') : null;
      const prev = sel('data-carousel-prev');
      const next = sel('data-carousel-next');
      const step = () => {
        const a = slides[0].getBoundingClientRect();
        const b = slides[1] ? slides[1].getBoundingClientRect() : null;
        return b ? Math.abs(b.left - a.left) : a.width;
      };
      const rtl = getComputedStyle(track).direction === 'rtl';
      let ticking = false;
      const update = () => {
        ticking = false;
        const pad = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
        const start = track.getBoundingClientRect().left + pad;
        let idx = 0; let best = Infinity;
        slides.forEach((s, i) => {
          const d = Math.abs(s.getBoundingClientRect().left - start);
          if (d < best) { best = d; idx = i; }
        });
        if (counter) counter.textContent = String(idx + 1);
        const max = track.scrollWidth - track.clientWidth;
        const pos = Math.abs(track.scrollLeft);
        if (prev) prev.disabled = pos <= 2;
        if (next) next.disabled = pos >= max - 2;
      };
      const go = (dir) => track.scrollBy({ left: dir * step() * (rtl ? -1 : 1), behavior: reduced.matches ? 'auto' : 'smooth' });
      if (prev) prev.addEventListener('click', () => go(-1));
      if (next) next.addEventListener('click', () => go(1));
      track.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      window.addEventListener('resize', () => requestAnimationFrame(update), { passive: true });
      update();
    });
  }

  function initOccasions() {
    const wrap = document.querySelector('[data-occasions]');
    if (!wrap || !desktop.matches) return;
    const panel = wrap.querySelector('[data-occasion-panel]');
    const cache = new Map();
    const show = (link) => {
      const tpl = link.querySelector('template[data-occasion-thumb]');
      if (!tpl || !panel) return;
      if (!cache.has(link)) cache.set(link, tpl.content.firstElementChild.cloneNode(true));
      panel.replaceChildren(cache.get(link));
    };
    const first = wrap.querySelector('[data-occasion]');
    if (first && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) { if (!panel.firstElementChild) show(first); io.disconnect(); }
      }, { rootMargin: '200px 0px' });
      io.observe(wrap);
    }
    wrap.querySelectorAll('[data-occasion]').forEach((link) => {
      link.addEventListener('pointerenter', () => show(link));
      link.addEventListener('focus', () => show(link));
    });
  }

  function initVideoCarousels() {
    const slides = document.querySelectorAll('[data-vcar-slide]');
    if (!slides.length) return;
    const conn = navigator.connection || {};
    const auto = !reduced.matches && !conn.saveData && !/(^|-)2g$/.test(conn.effectiveType || '');
    const labels = { pause: 'Pause clip', play: 'Play clip' };
    const attach = (video) => {
      if (video.querySelector('source')) return;
      const s = document.createElement('source');
      s.src = (!desktop.matches && video.dataset.srcMobile) ? video.dataset.srcMobile : video.dataset.src;
      s.type = 'video/mp4';
      video.appendChild(s);
      video.load();
    };
    const play = (frame) => {
      const video = frame.querySelector('video[data-src]');
      if (!video) return;
      attach(video);
      video.play().then(() => { video.classList.add('is-ready'); setToggleState(frame.querySelector('[data-vcar-toggle]'), true, labels); }).catch(() => {});
    };
    const pause = (frame) => {
      const video = frame.querySelector('video[data-src]');
      if (!video || video.paused) return;
      video.pause();
      setToggleState(frame.querySelector('[data-vcar-toggle]'), false, labels);
    };
    const io = ('IntersectionObserver' in window) ? new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const frame = e.target;
        frame.dataset.visible = e.isIntersecting ? '1' : '';
        if (e.isIntersecting) { if (auto && !frame.dataset.userPaused) play(frame); } else pause(frame);
      });
    }, { threshold: 0.6 }) : null;
    slides.forEach((frame) => {
      const btn = frame.querySelector('[data-vcar-toggle]');
      const video = frame.querySelector('video[data-src]');
      if (!video) return;
      if (btn) btn.addEventListener('click', () => {
        if (video.paused) { frame.dataset.userPaused = ''; play(frame); }
        else { frame.dataset.userPaused = '1'; pause(frame); }
      });
      if (io) io.observe(frame);
    });
    document.addEventListener('visibilitychange', () => {
      slides.forEach((frame) => {
        if (document.hidden) pause(frame);
        else if (auto && frame.dataset.visible && !frame.dataset.userPaused) play(frame);
      });
    });
  }

  function initFooter() {
    const cols = document.querySelectorAll('[data-footer-col]');
    const sync = () => cols.forEach((d) => { if (desktop.matches) d.open = true; });
    sync();
    desktop.addEventListener('change', sync);
  }

  function initKlaviyo() {
    const embed = document.querySelector('[data-klaviyo-embed]');
    const id = embed && embed.dataset.company;
    if (!embed || !id) return;
    let loaded = false;
    const load = () => {
      if (loaded) return; loaded = true;
      const s = document.createElement('script');
      s.async = true;
      s.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=' + encodeURIComponent(id);
      document.head.appendChild(s);
    };
    ['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach((ev) => window.addEventListener(ev, load, { once: true, passive: true }));
    window.addEventListener('load', () => idle(load), { once: true });
  }

  function initProductForm() {
    document.querySelectorAll('[data-variant-select]').forEach((select) => {
      const root = select.closest('[data-product]');
      const price = root && root.querySelector('[data-price]');
      select.addEventListener('change', () => {
        const opt = select.selectedOptions[0];
        if (price && opt && opt.dataset.price) price.textContent = opt.dataset.price;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', select.value);
        history.replaceState({}, '', url);
      });
    });
  }

  function init() {
    initHeader();
    initLocalization();
    initHeroVideo();
    initTapVideos();
    initCardHover();
    initCarousels();
    initOccasions();
    initFooter();
    initVideoCarousels();
    initKlaviyo();
    initProductForm();
    const success = document.querySelector('.gl-form__success');
    if (success) success.focus();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
